import {
  messagingLimitFromTier,
  qualityLevel,
  type QualityLevel,
  type WhatsAppNumberHealth,
} from '@neuroedge/shared';

/**
 * A WhatsApp number's sending health - Meta's messaging limit (customers the
 * business may start conversations with per rolling 24h) and quality
 * rating - with the next step to grow the limit. Meta raises limits by
 * itself; these hints follow Meta's published rules, which Meta does change.
 */

const QUALITY: Record<QualityLevel, { label: string; color: string }> = {
  HIGH: { label: 'High', color: 'var(--color-success)' },
  MEDIUM: { label: 'Medium', color: 'var(--color-accent-amber)' },
  LOW: { label: 'Low', color: 'var(--color-danger)' },
  UNKNOWN: { label: 'Unknown', color: 'var(--color-text-muted)' },
};

export function QualityBadge({ rating }: { rating: string | null }) {
  const q = QUALITY[qualityLevel(rating)];
  return (
    <span className="ne-badge" style={{ borderColor: q.color, color: q.color }}>
      Quality: {q.label}
    </span>
  );
}

/** "1,000 customers / day", "No daily limit", or "Unknown". */
export function limitLabel(tier: string | null): string {
  const limit = messagingLimitFromTier(tier);
  if (limit === 'UNLIMITED') return 'No daily limit';
  if (limit === null) return 'Unknown';
  return `${limit.toLocaleString()} customers / day`;
}

/** The one next step that matters most for this number. */
export function nextStep(health: WhatsAppNumberHealth): string {
  const quality = qualityLevel(health.qualityRating);
  const limit = messagingLimitFromTier(health.messagingLimitTier);
  if (quality === 'LOW') {
    return (
      'Quality is low, so Meta will not raise the limit. Pause marketing, check which ' +
      'messages customers block or report, and only message people who asked to hear from you.'
    );
  }
  if (limit === 'UNLIMITED') return 'No daily limit. Keep quality high to keep it that way.';
  if (limit === null) {
    return health.checkedAt
      ? "Meta didn't report a limit for this number - press Refresh again later. Campaigns " +
          'assume 250 customers a day.'
      : 'Refresh to read the limit from Meta. Until then campaigns assume 250 customers a day.';
  }
  const watch = quality === 'MEDIUM' ? ' Quality is medium - keep an eye on blocks.' : '';
  if (limit <= 250) {
    return (
      'To go past 250 customers a day: verify your business in Meta Business Manager ' +
      '(Security Centre → Business verification), or send 2,000 delivered messages with ' +
      'high-quality templates. Meta then raises it to 2,000.' +
      watch
    );
  }
  const next = limit < 10_000 ? '10,000' : limit < 100_000 ? '100,000' : 'unlimited';
  return (
    `To reach ${next}: message at least ${Math.ceil(limit / 2).toLocaleString()} different ` +
    'customers over 7 days while quality stays high. Meta checks this by itself and ' +
    'raises it within about 6 hours.' +
    watch
  );
}

/** Limit + quality + next step, for one number. */
export function NumberHealthSummary({ health }: { health: WhatsAppNumberHealth }) {
  return (
    <div style={{ display: 'grid', gap: 'var(--space-1)' }}>
      <div
        style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap', alignItems: 'center' }}
      >
        <span className="ne-badge">Messaging limit: {limitLabel(health.messagingLimitTier)}</span>
        <QualityBadge rating={health.qualityRating} />
        {health.checkedAt ? (
          <span style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' }}>
            Checked {new Date(health.checkedAt).toLocaleString()}
          </span>
        ) : null}
      </div>
      <span style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' }}>
        {nextStep(health)}
      </span>
    </div>
  );
}
